<template>
  <!-- Super admin client list: who has which modules, trials, and whether anyone signs in (US 9436) -->
  <div class="adm-page">
    <section class="home-hero adm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('admin_page.eyebrow') }}</div>
        <h1 class="home-hero__title" qid="clients-page-title">{{ $t('administration.clients.title') }}</h1>
        <p class="home-hero__summary" qid="clients-summary">{{ loading ? '\u00a0' : summaryText }}</p>

        <div class="home-search us-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="keyword"
                 class="home-search__input"
                 :placeholder="$t('admin_page.clients.search_placeholder')"
                 :aria-label="$t('admin_page.clients.search_placeholder')"
                 autocomplete="off"
                 qid="clients-search-input"
                 @input="debounceInput"
          />
          <span class="home-kbd">/</span>
        </div>

        <div class="home-hero__chips">
          <router-link to="/administration/clients/create" class="home-chip home-chip--light" qid="clients-create-button">
            <font-awesome-icon icon="plus"/>{{ $t('admin_page.actions.new_client') }}
          </router-link>
        </div>
      </div>
    </section>

    <div class="us-bar">
      <div class="us-seg" role="tablist">
        <button v-for="status in statuses"
                :key="status"
                type="button"
                role="tab"
                :class="{ 'is-on': filters.status === status }"
                :aria-selected="filters.status === status ? 'true' : 'false'"
                :qid="'clients-status-' + status"
                @click="setStatus(status)"
        >
          {{ $t('admin_page.clients.status.' + status) }}
          <em :class="{ 'is-red': status === 'trial_ended' && counts.trial_ended }">{{ counts[status] }}</em>
        </button>
      </div>
      <span class="us-grow"></span>
      <span class="adm-legend">
        <span class="adm-legend__on"></span>{{ $t('admin_page.clients.legend_on') }}
        <span class="adm-legend__off"></span>{{ $t('admin_page.clients.legend_off') }}
      </span>
    </div>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table adm-table">
          <thead>
            <tr>
              <th v-for="column in columns"
                  :key="column.key"
                  :class="[{ 'is-sortable': column.sort }, column.class]"
                  :aria-sort="column.sort && selectedSort === column.key ? (selectedDirection === 'asc' ? 'ascending' : 'descending') : null"
                  :qid="'clients-sort-' + column.key"
                  @click="column.sort && selectSort(column.key)"
              >
                {{ $t('admin_page.clients.table.' + column.key) }}
                <font-awesome-icon v-if="column.sort && selectedSort === column.key"
                                   :icon="selectedDirection === 'asc' ? 'caret-up' : 'caret-down'"
                                   class="ml-1"
                />
              </th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="client in tableItems" :key="client.id" qid="clients-row">
              <td>
                <router-link :to="{ name: 'administration.clientView', params: { slug: client.slug } }"
                             class="us-who adm-who"
                             qid="clients-name">
                  <img :src="avatarUrl(client)" class="adm-avatar" alt="">
                  <span class="adm-who__text">
                    <span class="us-who__name">{{ client.name }}</span>
                    <span v-if="clientMeta(client)" class="adm-who__meta">{{ clientMeta(client) }}</span>
                  </span>
                </router-link>
              </td>
              <td>
                <span class="adm-modules" qid="clients-modules">
                  <span v-for="module in modules"
                        :key="module.code"
                        class="adm-modules__dot"
                        :class="{ 'is-on': client[module.attr] }"
                        v-b-tooltip.hover
                        :title="$t(module.label) + ' – ' + (client[module.attr] ? $t('client_page.history.on') : $t('client_page.history.off'))"
                  ><font-awesome-icon :icon="module.icon" fixed-width/></span>
                  <span class="adm-modules__count">{{ moduleCount(client) }}/{{ modules.length }}</span>
                </span>
              </td>
              <td class="adm-num" qid="clients-users">{{ client.users_count }}</td>
              <td class="adm-num" qid="clients-partitions">{{ client.partitions_count || '–' }}</td>
              <td><TrialPill :client="client"/></td>
              <td>
                <div v-if="client.last_login_at"
                     class="us-last"
                     v-b-tooltip.hover
                     :title="formatDateTime(client.last_login_at)">
                  <span class="us-last__date" :class="{ 'adm-stale': isStale(client.last_login_at) }">{{ client.last_login_at | formatRelativeDate }}</span>
                </div>
                <span v-else class="us-muted">{{ $t('admin_page.never') }}</span>
              </td>
              <td class="us-muted">{{ formatDate(client.created_at) }}</td>
              <td class="us-table__actions">
                <router-link :to="{ name: 'administration.clientMembers', params: { slug: client.slug } }"
                             class="us-icon-btn"
                             v-b-tooltip.hover
                             :title="$t('admin_page.clients.actions.members')"
                             :aria-label="$t('admin_page.clients.actions.members')"
                             qid="clients-members-button"
                >
                  <font-awesome-icon icon="users"/>
                </router-link>
                <button type="button"
                        class="us-icon-btn"
                        v-b-tooltip.hover
                        :title="$t('admin_page.clients.actions.edit')"
                        :aria-label="$t('admin_page.clients.actions.edit')"
                        qid="clients-edit-button"
                        @click="$refs.edit.open(client)"
                >
                  <font-awesome-icon icon="pen"/>
                </button>
                <b-dropdown right
                            no-caret
                            variant="link"
                            toggle-class="us-icon-btn"
                            qid="clients-more-button"
                >
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-h"/>
                    <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                  </template>
                  <b-dropdown-item :to="{ name: 'administration.clientView', params: { slug: client.slug } }">
                    <font-awesome-icon icon="external-link-alt" class="mr-2"/>{{ $t('admin_page.clients.actions.open') }}
                  </b-dropdown-item>
                  <b-dropdown-item variant="danger" qid="clients-delete-button" @click="showConfirmDeleteModal(client)">
                    <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('admin_page.clients.actions.delete') }}
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row">
              <td :colspan="columns.length + 1" class="us-empty">{{ $t('system.no_results') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="us-foot">
        <span class="us-foot__count">{{ pageText }}</span>
        <multiselect v-model="perPage"
                     class="us-filter us-filter--small"
                     :options="perPageOptions"
                     track-by="value"
                     label="value"
                     :allow-empty="false"
                     :placeholder="selectedPerPage"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="clients-per-page"
                     @select="perPageChanged"
        ></multiselect>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      @change="loadClients"
                      limit="5"
        ></b-pagination>
      </div>
    </div>

    <ClientEditPanel ref="edit" :modules="moduleList" @saved="loadClients(currentPage)"></ClientEditPanel>

    <!-- Delete client -->
    <b-modal ref="delete-client" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-client-title">{{ $t('administration.clients.delete.title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.clients.delete.cancel')"
                  @click="hideConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <img :src="avatarUrl(itemToDelete)" class="adm-avatar adm-avatar--lg" alt="">
        <div>
          <b>{{ itemToDelete.name }}</b>
          <div class="us-muted">
            {{ $tc('admin_page.clients.users_count', itemToDelete.users_count || 0, { count: itemToDelete.users_count || 0 }) }}
            · {{ $tc('admin_page.clients.partitions_count', itemToDelete.partitions_count || 0, { count: itemToDelete.partitions_count || 0 }) }}
          </div>
        </div>
      </div>
      <div class="us-note us-note--warn mt-3">
        <font-awesome-icon icon="exclamation-triangle"/>
        <span>{{ $t('admin_page.clients.delete_text') }}</span>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-client-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('administration.clients.delete.cancel') }}
        </b-button>
        <b-button variant="danger"
                  :disabled="deleting"
                  qid="delete-client-submit-button"
                  @click="confirmDeleteClient(itemToDelete)">
          <font-awesome-icon v-if="deleting" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('admin_page.clients.actions.delete') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import {config} from '@/config.js';
import {userAvatarUrl} from '@/util/userAvatar.js';
import {MODULES} from '@/util/adminModules.js';
import ClientEditPanel from '@/components/administration/ClientEditPanel.vue';
import TrialPill from '@/components/administration/TrialPill.vue';

const moment = require('moment');

const STATUSES = ['all', 'trial', 'trial_ended', 'inactive'];
// Days without a sign-in before the date turns red, as for inactive clients
const STALE_DAYS = 90;

export default {
  name: 'Clients',
  components: {
    ClientEditPanel,
    TrialPill
  },
  data() {
    let query = this.$route.query;
    return {
      tableItems: [],
      counts: { all: 0, trial: 0, trial_ended: 0, inactive: 0 },
      statuses: STATUSES,
      modules: MODULES,
      moduleList: [],
      columns: [
        { key: 'name', sort: true },
        { key: 'modules', class: 'adm-table__modules' },
        { key: 'users_count', sort: true, class: 'adm-num' },
        { key: 'partitions', class: 'adm-num' },
        { key: 'plan' },
        { key: 'last_login_at', sort: true },
        { key: 'created_at', sort: true }
      ],
      keyword: '',
      filters: {
        status: STATUSES.indexOf(query.status) !== -1 ? query.status : 'all'
      },
      selectedSort: query.sort || 'name',
      selectedDirection: query.sort ? 'desc' : 'asc',
      perPageOptions: [
        { value: '10' },
        { value: '20' },
        { value: '50' },
        { value: '100' }
      ],
      perPage: null,
      selectedPerPage: '20',
      currentPage: 1,
      totalRows: 0,
      itemToDelete: {},
      loading: true,
      deleting: false
    }
  },
  computed: {
    summaryText() {
      let sentences = [this.$tc('admin_page.clients.summary', this.counts.all, { count: this.counts.all })];
      if (this.counts.trial) {
        sentences.push(this.$tc('admin_page.clients.summary_trial', this.counts.trial, { count: this.counts.trial }));
      }
      if (this.counts.inactive) {
        sentences.push(this.$tc('admin_page.clients.summary_inactive', this.counts.inactive, { count: this.counts.inactive }));
      }
      return sentences.join(' ');
    },
    pageText() {
      let from = (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
      let to = Math.min(from + Number(this.selectedPerPage) - 1, this.totalRows);
      return this.totalRows ? this.$t('users_page.pagination', { from: from, to: to, total: this.totalRows }) : '';
    }
  },
  methods: {
    debounceInput: _.debounce(function() {
      this.loadClients();
    }, 400),
    loadClients(page) {
      let self = this;
      this.currentPage = page || 1;
      piincHttp.get('clients', { params: {
        page: this.currentPage,
        per_page: this.selectedPerPage,
        without_partition: 1,
        with_stats: 1,
        keyword: this.keyword || undefined,
        status: this.filters.status !== 'all' ? this.filters.status : undefined,
        sort: this.selectedSort,
        order: this.selectedDirection
      }}).then(function(response) {
        self.tableItems = response.data.items;
        self.totalRows = response.data.total_count;
        self.counts = response.data.status_counts || self.counts;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    loadModules() {
      let self = this;
      piincHttp.get('modules').then(function(response) {
        self.moduleList = response.data.items;
      }, function() {});
    },
    setStatus(status) {
      this.filters.status = status;
      this.loadClients();
    },
    selectSort(sort) {
      // Same column flips the direction; names start A–Z, numbers and dates newest first
      if (sort === this.selectedSort) {
        this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
      } else {
        this.selectedSort = sort;
        this.selectedDirection = sort === 'name' ? 'asc' : 'desc';
      }
      this.loadClients();
    },
    perPageChanged(perPage) {
      this.selectedPerPage = perPage.value;
      this.loadClients();
    },
    avatarUrl(client) {
      return userAvatarUrl(client);
    },
    clientMeta(client) {
      return [client.cvr, client.city || client.country, client.reference_number].filter(Boolean).join(' · ');
    },
    moduleCount(client) {
      return this.modules.filter(module => client[module.attr]).length;
    },
    isStale(date) {
      return moment().diff(moment(date), 'days') >= STALE_DAYS;
    },
    formatDate(date) {
      return date ? this.$options.filters.formatDate(date, config.dateFormat) : '';
    },
    formatDateTime(date) {
      return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
    },
    showConfirmDeleteModal(client) {
      this.itemToDelete = client;
      this.$refs['delete-client'].show();
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {};
      this.$refs['delete-client'].hide();
    },
    confirmDeleteClient(client) {
      let self = this;
      this.deleting = true;
      piincHttp.delete('clients/' + client.slug).then(function() {
        self.deleting = false;
        self.hideConfirmDeleteModal();
        self.loadClients(self.currentPage);
        self.$toastr('success', self.$t('administration.clients.delete.success'));
      }, function() {
        self.deleting = false;
      });
    },
    // "/" focuses the search, unless you are typing somewhere
    onKeydown(event) {
      let tag = event.target.tagName;
      if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !event.target.isContentEditable) {
        event.preventDefault();
        this.$refs.search.focus();
      }
    }
  },
  created() {
    if (this.$route.params.reload) {
      window.location.reload();
    } else {
      this.loadClients();
      this.loadModules();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
</script>
